/** Disk snapshots are hints for rendering. They never establish identity or access. */
export const CACHE_DB = "l9-chat-ui-cache";
export const CACHE_ENABLED_FLAG = "l9-ui-cache-enabled";
export const CACHE_MESSAGES_FLAG = "l9-ui-cache-message-tails";
const PURGE_MARKER = "l9-ui-cache-needsPurge";
const VERSION = 1;
const MiB = 1024 * 1024;
const GLOBAL_CAP = 12 * MiB;
const MAX_ENTRIES = 64;
const STORE_CONFIG = {
  sessionPages: { cap: 1.5 * MiB, count: 1, ttl: 24 * 3600_000 },
  agentCatalogs: { cap: .5 * MiB, count: 1, ttl: 6 * 3600_000 },
  displayPrefs: { cap: .25 * MiB, count: 1, ttl: 6 * 3600_000 },
  projectLists: { cap: 1 * MiB, count: 2, ttl: 6 * 3600_000 },
  contextDetails: { cap: .75 * MiB, count: 8, ttl: 6 * 3600_000 },
  viewSummaries: { cap: .5 * MiB, count: 4, ttl: 6 * 3600_000 },
  messageTails: { cap: 6 * MiB, count: 6, ttl: 24 * 3600_000 },
  taskPages: { cap: 1.5 * MiB, count: 24, ttl: 8 * 3600_000 },
} as const;
export type CacheStore = keyof typeof STORE_CONFIG;
export const CACHE_BUDGETS = STORE_CONFIG;
export const CACHE_GLOBAL_CAP = GLOBAL_CAP;
export const CACHE_MAX_ENTRIES = MAX_ENTRIES;

interface Entry<T = unknown> {
  scope: string;
  kind: string;
  resourceKey: string;
  fetchedAt: number;
  expiresAt: number;
  lastAccessed: number;
  estimatedBytes: number;
  schemaVersion: number;
  payload: T;
}

let scope: string | null = null;
let epoch = 0;
let dbPromise: Promise<IDBDatabase | null> | null = null;
let writes: Promise<void> = Promise.resolve();
let diskBroken = false;
let writesDisabled = false;
let lastSweep = 0;
const confirmedProjectIds = new Set<string>();
export function isProjectLiveConfirmed(id: string): boolean { return confirmedProjectIds.has(id); }
export function setLiveConfirmedProjects(ids: Iterable<string>): void {
  confirmedProjectIds.clear();
  for (const id of ids) if (typeof id === "string" && id) confirmedProjectIds.add(id);
}
export function confirmProjectDetail(id: string): void { if (id) confirmedProjectIds.add(id); }
const listeners = new Set<() => void>();
const channel = typeof window !== "undefined" && typeof BroadcastChannel !== "undefined"
  ? new BroadcastChannel(CACHE_DB) : null;
const names = Object.keys(STORE_CONFIG) as CacheStore[];
const storage = () => { try { return window.localStorage; } catch { return null; } };
function getFlag(key: string): string | null { try { return storage()?.getItem(key) ?? null; } catch { return null; } }
function setPurgeMarker(value: boolean): void { try { if (value) storage()?.setItem(PURGE_MARKER, "1"); else storage()?.removeItem(PURGE_MARKER); } catch { /* Storage can be disabled. */ } }
export function cacheEnabled(): boolean { return getFlag(CACHE_ENABLED_FLAG) !== "0"; }
export function messageCacheEnabled(): boolean { return cacheEnabled() && getFlag(CACHE_MESSAGES_FLAG) === "1"; }
export function cacheScope(): string | null { return scope; }
export function clearCacheOnAuthFailure(): void { setVerifiedCacheIdentity(null); }
export function cacheEpoch(): number { return epoch; }
export function subscribeCacheScope(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
function announce() { epoch++; for (const listener of listeners) listener(); }

/** /api/me is required; AuthKit's stable user ID must agree when supplied. */
export function setVerifiedCacheIdentity(me: {
  tenant?: string; user_id?: string; role?: string;
  impersonator?: unknown; is_platform_admin?: boolean;
  is_tenant_admin?: boolean; tenant_admin_tenant?: string | null;
} | null, authUserId?: string | null): void {
  const next = me?.tenant?.trim() && me.user_id?.trim() && me.role?.trim()
    && (!authUserId || authUserId === me.user_id) && !me.impersonator
    ? JSON.stringify([typeof location === "undefined" ? "" : location.origin, me.tenant, me.user_id,
      me.role, me.is_platform_admin ?? null, me.is_tenant_admin ?? null, me.tenant_admin_tenant ?? null, "direct"])
    : null;
  if (next === scope) return;
  const previous = scope;
  scope = next;
  confirmedProjectIds.clear();
  announce();
  if (previous) void purgeScope(previous);
  if (next) void sweep(true);
}

export function estimateCacheBytes(payload: unknown): number {
  return 2 * new TextEncoder().encode(JSON.stringify(payload)).byteLength + 256;
}

function openDb(force = false): Promise<IDBDatabase | null> {
  if (diskBroken || (!force && !cacheEnabled()) || typeof indexedDB === "undefined") return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    let done = false;
    const finish = (value: IDBDatabase | null) => {
      if (done) { value?.close(); return; }
      done = true; clearTimeout(timer); resolve(value);
    };
    const timer = setTimeout(() => { diskBroken = true; finish(null); }, 400);
    try {
      const request = indexedDB.open(CACHE_DB, VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        for (const name of names) {
          if (db.objectStoreNames.contains(name)) continue;
          const store = db.createObjectStore(name, { keyPath: ["scope", "kind", "resourceKey"] });
          store.createIndex("expires", ["scope", "expiresAt"]);
          store.createIndex("access", ["scope", "lastAccessed"]);
        }
        if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta", { keyPath: "key" });
      };
      request.onsuccess = () => {
        request.result.onversionchange = () => { request.result.close(); dbPromise = null; };
        finish(request.result);
      };
      request.onerror = request.onblocked = () => { diskBroken = true; finish(null); };
    } catch { diskBroken = true; finish(null); }
  });
  return dbPromise;
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
function settled(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = tx.onerror = () => reject(tx.error);
  });
}
function key(kind: string) { return `v${VERSION}:${kind}`; }
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const rows = (value: unknown, limit: number, detail: (item: Record<string, unknown>) => boolean): boolean => Array.isArray(value)
  && value.length <= limit && value.every((item) => record(item) && typeof item.id === "string" && detail(item));
const idList = (value: unknown): boolean => Array.isArray(value)
  && value.length <= 100 && value.every((item) => typeof item === "string" && item.length > 0);
/** Reject corrupt or old-format rows before any hook can dereference them. */
export function validCachePayload(store: CacheStore, value: unknown, kind?: string): boolean {
  if (store === "agentCatalogs") return rows(value, 100, (item) => typeof item.name === "string" && (!item.views || Array.isArray(item.views)));
  if (store === "viewSummaries") return rows(value, 100, (item) => typeof item.title === "string"
    && typeof item.icon === "string" && typeof item.type === "string" && typeof item.agent_id === "string" && typeof item.order === "number");
  if (!record(value)) return false;
  switch (store) {
    case "sessionPages": return rows(value.sessions, 50, (item) => typeof item.title === "string");
    case "projectLists": return rows(value.projects, 50, (item) => typeof item.name === "string");
    case "displayPrefs": return idList(value.agentPins)
      && record(value.sidebarPinOrder) && idList(value.sidebarPinOrder.agentIds)
      && idList(value.sidebarPinOrder.projectIds) && record(value.preferences)
      && (value.preferences.submit_shortcut === "enter" || value.preferences.submit_shortcut === "ctrl_enter");
    case "contextDetails": return typeof value.id === "string" && (kind === "session"
      ? typeof value.title === "string" && (value.parent_session_id === null || typeof value.parent_session_id === "string")
      : kind === "project" && typeof value.name === "string");
    case "messageTails": return rows(value.messages, 40, (item) => typeof item.content === "string" && typeof item.run_id === "string");
    case "taskPages": return rows(value.tasks, 100, (item) => typeof item.project_id === "string" && typeof item.title === "string" && typeof item.status === "string");
  }
}
function deadline<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([promise.catch(() => fallback), new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms))]);
}

export async function readCache<T>(store: CacheStore, kind: string, resourceKey: string): Promise<T | null> {
  const captured = scope;
  if (!captured || getFlag(PURGE_MARKER) || !cacheEnabled() || (store === "messageTails" && !messageCacheEnabled())) return null;
  return deadline((async () => {
    const db = await openDb();
    if (!db || captured !== scope) return null;
    const tx = db.transaction(store, "readonly");
    const entry = await requestValue<Entry<T> | undefined>(tx.objectStore(store).get([captured, key(kind), resourceKey]));
    if (!entry || captured !== scope || entry.schemaVersion !== VERSION || entry.expiresAt <= Date.now()) return null;
    if (!validCachePayload(store, entry.payload, kind)) {
      void invalidateCache(store, kind, resourceKey);
      return null;
    }
    if (Date.now() - entry.lastAccessed >= 60_000) void touch(store, entry);
    return entry.payload;
  })(), 250, null);
}

async function touch(store: CacheStore, entry: Entry): Promise<void> {
  try {
    const db = await openDb(); if (!db || entry.scope !== scope) return;
    const tx = db.transaction(store, "readwrite");
    tx.objectStore(store).put({ ...entry, lastAccessed: Date.now() });
    await settled(tx);
  } catch { /* Access timestamps never block requests. */ }
}

function enqueue(work: () => Promise<void>): Promise<void> {
  const result = writes.then(work).catch(() => undefined);
  writes = result;
  return result;
}
function ordered(a: Entry, b: Entry): number {
  return a.lastAccessed - b.lastAccessed || a.fetchedAt - b.fetchedAt
    || a.scope.localeCompare(b.scope) || a.kind.localeCompare(b.kind)
    || a.resourceKey.localeCompare(b.resourceKey);
}
/** Pure cap planner; recomputing all records repairs interrupted or cross-tab accounting. */
export function planEvictions(rows: Array<{ store: CacheStore; entry: Entry }>, factor = 1): Array<{ store: CacheStore; entry: Entry }> {
  const now = Date.now();
  const removed = new Set(rows.filter(({ entry }) => entry.expiresAt <= now));
  const live = () => rows.filter((row) => !removed.has(row));
  const trim = (candidates: typeof rows, bytes: number, count: number) => {
    const orderedRows = [...candidates].sort((a, b) => ordered(a.entry, b.entry));
    let total = orderedRows.reduce((sum, row) => sum + row.entry.estimatedBytes, 0);
    let size = orderedRows.length;
    const trigger = bytes * .8;
    if (total < trigger && size <= count) return;
    for (const row of orderedRows) {
      if (total <= bytes * .7 && size <= count) break;
      removed.add(row); total -= row.entry.estimatedBytes; size--;
    }
  };
  for (const store of names) {
    const config = STORE_CONFIG[store];
    trim(live().filter((row) => row.store === store), config.cap * factor, config.count);
  }
  // Context details reserve no more than two slots for project names.
  const projects = live().filter((row) => row.store === "contextDetails" && row.entry.kind === key("project"));
  for (const row of projects.sort((a, b) => ordered(a.entry, b.entry)).slice(0, Math.max(0, projects.length - 2))) removed.add(row);
  const tasks = live().filter((row) => row.store === "taskPages");
  for (const projectId of [...new Set(tasks.map((row) => row.entry.resourceKey.split(":")[0]))]) {
    const pages = tasks.filter((row) => row.entry.resourceKey.startsWith(`${projectId}:`)).sort((a, b) => ordered(a.entry, b.entry));
    for (const row of pages.slice(0, Math.max(0, pages.length - 6))) removed.add(row);
  }
  const projectIds = [...new Set(tasks.map((row) => row.entry.resourceKey.split(":")[0]))];
  if (projectIds.length > 4) {
    const oldest = projectIds.map((id) => ({ id, at: Math.max(...tasks.filter((row) => row.entry.resourceKey.startsWith(`${id}:`)).map((row) => row.entry.lastAccessed)) })).sort((a, b) => a.at - b.at);
    for (const { id } of oldest.slice(0, projectIds.length - 4)) for (const row of tasks) if (row.entry.resourceKey.startsWith(`${id}:`)) removed.add(row);
  }
  trim(live(), GLOBAL_CAP * factor, MAX_ENTRIES);
  return [...removed];
}

async function pressureFactor(): Promise<number> {
  try {
    const estimate = await navigator.storage?.estimate?.();
    if (estimate?.quota && estimate.usage !== undefined && (estimate.usage / estimate.quota > .8 || estimate.quota - estimate.usage < 20 * MiB)) return .5;
    writesDisabled = false;
  } catch { /* Storage estimates are advisory. */ }
  return 1;
}
async function allRows(tx: IDBTransaction): Promise<Array<{ store: CacheStore; entry: Entry }>> {
  const lists = await Promise.all(names.map(async (store) => {
    const entries = await requestValue<Entry[]>(tx.objectStore(store).getAll());
    return entries.map((entry) => ({ store, entry }));
  }));
  return lists.flat();
}
async function compact(db: IDBDatabase, incoming?: { store: CacheStore; entry: Entry }, factor = 1): Promise<void> {
  const tx = db.transaction([...names, "meta"], "readwrite");
  const rows = await allRows(tx);
  if (incoming) {
    const old = rows.find((row) => row.store === incoming.store && row.entry.scope === incoming.entry.scope && row.entry.kind === incoming.entry.kind && row.entry.resourceKey === incoming.entry.resourceKey);
    if (old) {
      rows.splice(rows.indexOf(old), 1);
      tx.objectStore(old.store).delete([old.entry.scope, old.entry.kind, old.entry.resourceKey]);
    }
    rows.push(incoming);
  }
  const evicted = planEvictions(rows, factor);
  for (const row of evicted) tx.objectStore(row.store).delete([row.entry.scope, row.entry.kind, row.entry.resourceKey]);
  if (incoming && !evicted.includes(incoming)) tx.objectStore(incoming.store).put(incoming.entry);
  const retained = rows.filter((row) => !evicted.includes(row));
  tx.objectStore("meta").put({ key: "accounting", count: retained.length, bytes: retained.reduce((sum, row) => sum + row.entry.estimatedBytes, 0), sweptAt: Date.now() });
  await settled(tx);
}

export async function writeCache<T>(store: CacheStore, kind: string, resourceKey: string, payload: T): Promise<void> {
  const captured = scope;
  if (!captured || getFlag(PURGE_MARKER) || writesDisabled || !cacheEnabled()
    || (store === "messageTails" && !messageCacheEnabled()) || !validCachePayload(store, payload, kind)) return;
  let bytes: number;
  try { bytes = estimateCacheBytes(payload); } catch { return; }
  const config = STORE_CONFIG[store];
  const perEntry = store === "messageTails" ? 1 * MiB : config.cap;
  if (bytes > perEntry || bytes > config.cap * .7) return;
  const at = Date.now();
  const entry: Entry<T> = { scope: captured, kind: key(kind), resourceKey, fetchedAt: at, expiresAt: at + config.ttl, lastAccessed: at, estimatedBytes: bytes, schemaVersion: VERSION, payload };
  await enqueue(async () => {
    if (captured !== scope) return;
    const db = await openDb(); if (!db) return;
    if (captured !== scope) return;
    const factor = await pressureFactor();
    if (captured !== scope) return;
    if (factor < 1) { await compact(db, undefined, factor); writesDisabled = true; return; }
    try { await compact(db, { store, entry }, factor); }
    catch (error) {
      if ((error as DOMException)?.name === "QuotaExceededError") {
        try { await compact(db, undefined, .25); await compact(db, { store, entry }, .25); } catch { /* Retry once. */ }
        writesDisabled = true;
      } else diskBroken = true;
    }
  });
}

export async function invalidateCache(store: CacheStore, kind?: string, resourceKey?: string, broadcast = true): Promise<void> {
  const captured = scope; if (!captured) return;
  await enqueue(async () => {
    const db = await openDb(); if (!db) return;
    const tx = db.transaction(store, "readwrite");
    const rows = await requestValue<Entry[]>(tx.objectStore(store).getAll());
    for (const entry of rows) if (entry.scope === captured && (!kind || entry.kind === key(kind)) && (!resourceKey || entry.resourceKey === resourceKey)) tx.objectStore(store).delete([entry.scope, entry.kind, entry.resourceKey]);
    await settled(tx);
  });
  if (broadcast) try { channel?.postMessage({ scope: captured, store, kind, resourceKey }); } catch { /* Optional. */ }
}

if (channel) channel.onmessage = (event: MessageEvent) => {
  const data = event.data as { scope?: unknown; store?: unknown; kind?: unknown; resourceKey?: unknown } | null;
  if (!data || data.scope !== scope || !names.includes(data.store as CacheStore)) return;
  void invalidateCache(data.store as CacheStore,
    typeof data.kind === "string" ? data.kind : undefined,
    typeof data.resourceKey === "string" ? data.resourceKey : undefined, false);
};

async function purgeScope(target: string): Promise<void> {
  await enqueue(async () => {
    const db = await openDb(true); if (!db) return;
    const tx = db.transaction(names, "readwrite");
    const rows = await allRows(tx);
    for (const { store, entry } of rows) if (entry.scope === target) tx.objectStore(store).delete([entry.scope, entry.kind, entry.resourceKey]);
    await settled(tx);
  });
}

async function clearAllDiskRows(): Promise<void> {
  await deadline(enqueue(async () => {
    const db = await openDb(true); if (!db) throw new Error("Cache unavailable");
    const tx = db.transaction([...names, "meta"], "readwrite");
    for (const store of [...names, "meta"]) tx.objectStore(store).clear();
    await settled(tx);
    setPurgeMarker(false);
  }), 1000, undefined);
}

export async function purgeAllUiCache(): Promise<void> {
  setPurgeMarker(true);
  scope = null;
  confirmedProjectIds.clear();
  announce();
  await clearAllDiskRows();
}

/** Delete only the chat UI database when entering or leaving impersonation. */
export function deleteChatUiDatabase(factory: Pick<IDBFactory, "deleteDatabase">, timeoutMs = 1500): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let done = false;
    const finish = (error?: Error) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (error) reject(error); else resolve();
    };
    const timer = setTimeout(() => finish(new Error("Chat UI cache deletion timed out")), timeoutMs);
    try {
      const request = factory.deleteDatabase(CACHE_DB);
      request.onsuccess = () => finish();
      request.onerror = () => finish(new Error("Chat UI cache deletion failed"));
      request.onblocked = () => finish(new Error("Chat UI cache deletion blocked"));
    } catch { finish(new Error("Chat UI cache deletion failed")); }
  });
}

export async function resetImpersonationCache(impersonating: boolean, force = false): Promise<void> {
  if (typeof indexedDB === "undefined") return; // Cache is already unavailable.
  const mode = impersonating ? "impersonating" : "direct";
  // A missing or unreadable marker requires a reset. The marker lives inside
  // this cache database, so no unrelated browser storage is changed.
  let previous: string | undefined;
  if (!force) {
    try {
      const db = await openDb(true);
      if (db) previous = await deadline(requestValue<{ value?: string } | undefined>(
        db.transaction("meta", "readonly").objectStore("meta").get("impersonationMode")
      ).then((row) => row?.value), 500, undefined);
    } catch { /* An unreadable cache must be deleted. */ }
  }
  if (previous === mode) return;

  diskBroken = true; // Prevent reads and new opens while deletion is pending.
  const oldDb = dbPromise;
  dbPromise = null;
  oldDb?.then((db) => db?.close()).catch(() => undefined);
  scope = null;
  confirmedProjectIds.clear();
  announce();
  try {
    await deleteChatUiDatabase(indexedDB);
    diskBroken = false;
    const db = await openDb(true);
    if (!db) throw new Error("Chat UI cache unavailable");
    const tx = db.transaction("meta", "readwrite");
    tx.objectStore("meta").put({ key: "impersonationMode", value: mode });
    const recorded = await deadline(settled(tx).then(() => true), 500, false);
    if (!recorded) throw new Error("Chat UI cache marker write timed out");
  } catch (error) {
    diskBroken = true; // Network-only for this page if deletion is blocked.
    throw error;
  }
}

export async function sweep(force = false): Promise<void> {
  if (!scope || (!force && Date.now() - lastSweep < 15 * 60_000)) return;
  lastSweep = Date.now();
  if (getFlag(PURGE_MARKER)) {
    await clearAllDiskRows();
    if (getFlag(PURGE_MARKER)) return;
  }
  await enqueue(async () => {
    const db = await openDb(true); if (!db) return;
    const factor = await pressureFactor();
    await compact(db, undefined, factor);
    const tx = db.transaction([...names, "meta"], "readwrite");
    const rows = await allRows(tx);
    for (const { store, entry } of rows.slice(0, 100)) if (entry.scope !== scope) tx.objectStore(store).delete([entry.scope, entry.kind, entry.resourceKey]);
    const retained = rows.filter(({ entry }) => entry.scope === scope);
    tx.objectStore("meta").put({ key: "accounting", count: retained.length, bytes: retained.reduce((sum, row) => sum + row.entry.estimatedBytes, 0), sweptAt: Date.now() });
    await settled(tx);
  });
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") void sweep(); });
  setInterval(() => { if (document.visibilityState === "visible") void sweep(); }, 15 * 60_000);
}
